export type AudioBusId = "master" | "music" | "weather" | "enemy" | "ui";

export type AudioCueId =
  | "lightning-charge"
  | "lightning-strike"
  | "enemy-impact"
  | "enemy-defeated"
  | "purchase"
  | "milestone"
  | "boss-warning"
  | "rain"
  | "wind"
  | "hail"
  | "reaction"
  | "ui-confirm"
  | "ui-cancel";

export interface AudioPlayOptions {
  readonly volume?: number;
  readonly pan?: number;
  readonly rate?: number;
}

export interface AudioPort {
  unlock(): Promise<void>;
  play(cue: AudioCueId, options?: AudioPlayOptions): void;
  setBusVolume(bus: AudioBusId, value: number): void;
  pauseAll(): void;
  resumeAll(): void;
  destroy(): void;
}

interface CueRecipe {
  readonly bus: AudioBusId;
  readonly wave: OscillatorType;
  readonly frequency: number;
  readonly endFrequency: number;
  readonly duration: number;
  readonly gain: number;
  readonly noise?: number;
  readonly maxConcurrent: number;
}

const RECIPES: Readonly<Record<AudioCueId, CueRecipe>> = {
  "lightning-charge": {
    bus: "weather",
    wave: "sine",
    frequency: 180,
    endFrequency: 620,
    duration: 0.09,
    gain: 0.08,
    maxConcurrent: 2,
  },
  "lightning-strike": {
    bus: "weather",
    wave: "sawtooth",
    frequency: 980,
    endFrequency: 90,
    duration: 0.12,
    gain: 0.13,
    noise: 0.09,
    maxConcurrent: 4,
  },
  "enemy-impact": {
    bus: "enemy",
    wave: "triangle",
    frequency: 130,
    endFrequency: 48,
    duration: 0.22,
    gain: 0.14,
    maxConcurrent: 3,
  },
  "enemy-defeated": {
    bus: "enemy",
    wave: "sine",
    frequency: 300,
    endFrequency: 780,
    duration: 0.08,
    gain: 0.035,
    maxConcurrent: 5,
  },
  purchase: {
    bus: "ui",
    wave: "sine",
    frequency: 440,
    endFrequency: 880,
    duration: 0.14,
    gain: 0.08,
    maxConcurrent: 2,
  },
  milestone: {
    bus: "ui",
    wave: "triangle",
    frequency: 260,
    endFrequency: 1040,
    duration: 0.45,
    gain: 0.12,
    maxConcurrent: 1,
  },
  "boss-warning": {
    bus: "ui",
    wave: "square",
    frequency: 96,
    endFrequency: 72,
    duration: 0.32,
    gain: 0.09,
    maxConcurrent: 1,
  },
  rain: {
    bus: "weather",
    wave: "sine",
    frequency: 520,
    endFrequency: 310,
    duration: 0.24,
    gain: 0.025,
    noise: 0.08,
    maxConcurrent: 2,
  },
  wind: {
    bus: "weather",
    wave: "sine",
    frequency: 170,
    endFrequency: 480,
    duration: 0.34,
    gain: 0.03,
    noise: 0.035,
    maxConcurrent: 2,
  },
  hail: {
    bus: "weather",
    wave: "triangle",
    frequency: 1450,
    endFrequency: 520,
    duration: 0.08,
    gain: 0.06,
    maxConcurrent: 4,
  },
  reaction: {
    bus: "weather",
    wave: "sawtooth",
    frequency: 220,
    endFrequency: 1320,
    duration: 0.38,
    gain: 0.1,
    maxConcurrent: 2,
  },
  "ui-confirm": {
    bus: "ui",
    wave: "sine",
    frequency: 560,
    endFrequency: 760,
    duration: 0.07,
    gain: 0.045,
    maxConcurrent: 2,
  },
  "ui-cancel": {
    bus: "ui",
    wave: "triangle",
    frequency: 280,
    endFrequency: 180,
    duration: 0.08,
    gain: 0.04,
    maxConcurrent: 2,
  },
};

const clamp = (value: number, min = 0, max = 1): number =>
  Math.min(max, Math.max(min, value));

export class WebAudioAdapter implements AudioPort {
  private context: AudioContext | undefined;
  private readonly busVolumes: Record<AudioBusId, number> = {
    master: 0.75,
    music: 0.28,
    weather: 0.72,
    enemy: 0.58,
    ui: 0.64,
  };
  private readonly activeCues = new Map<AudioCueId, number>();
  private suspendedByApp = false;

  async unlock(): Promise<void> {
    this.context ??= new AudioContext({ latencyHint: "interactive" });
    if (this.context.state === "suspended") await this.context.resume();
    this.suspendedByApp = false;
  }

  play(cue: AudioCueId, options: AudioPlayOptions = {}): void {
    const context = this.context;
    if (
      context === undefined ||
      context.state !== "running" ||
      this.suspendedByApp
    )
      return;
    const recipe = RECIPES[cue];
    const active = this.activeCues.get(cue) ?? 0;
    if (active >= recipe.maxConcurrent) return;
    this.activeCues.set(cue, active + 1);

    const now = context.currentTime;
    const duration = recipe.duration / clamp(options.rate ?? 1, 0.5, 2);
    const output = context.createGain();
    const panner = context.createStereoPanner();
    const oscillator = context.createOscillator();
    oscillator.type = recipe.wave;
    oscillator.frequency.setValueAtTime(recipe.frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(1, recipe.endFrequency),
      now + duration,
    );
    const busGain =
      this.busVolumes.master *
      this.busVolumes[recipe.bus] *
      clamp(options.volume ?? 1);
    output.gain.setValueAtTime(Math.max(0.0001, recipe.gain * busGain), now);
    output.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    panner.pan.value = clamp(options.pan ?? 0, -1, 1);
    oscillator.connect(output).connect(panner).connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);

    if (recipe.noise !== undefined)
      this.playNoise(context, now, duration, recipe.noise * busGain, panner);
    oscillator.addEventListener(
      "ended",
      () => {
        oscillator.disconnect();
        output.disconnect();
        panner.disconnect();
        this.activeCues.set(
          cue,
          Math.max(0, (this.activeCues.get(cue) ?? 1) - 1),
        );
      },
      { once: true },
    );
  }

  setBusVolume(bus: AudioBusId, value: number): void {
    this.busVolumes[bus] = clamp(value);
  }

  pauseAll(): void {
    this.suspendedByApp = true;
    void this.context?.suspend();
  }

  resumeAll(): void {
    this.suspendedByApp = false;
    void this.context?.resume();
  }

  destroy(): void {
    this.activeCues.clear();
    void this.context?.close();
    this.context = undefined;
  }

  private playNoise(
    context: AudioContext,
    now: number,
    duration: number,
    gainValue: number,
    destination: AudioNode,
  ): void {
    const frames = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, frames, context.sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < frames; index += 1)
      channel[index] = Math.random() * 2 - 1;
    const source = context.createBufferSource();
    const gain = context.createGain();
    gain.gain.setValueAtTime(Math.max(0.0001, gainValue), now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.buffer = buffer;
    source.connect(gain).connect(destination);
    source.start(now);
    source.addEventListener(
      "ended",
      () => {
        source.disconnect();
        gain.disconnect();
      },
      { once: true },
    );
  }
}

export const createAudioPort = (): AudioPort => new WebAudioAdapter();
